import {useMutation, useQueryClient} from '@tanstack/react-query'
import {getAsCauseType, postAsRegister, putAsCancel} from 'utils/apiCalling/reactQueryApi'
import {
  asTargetLabel,
  isAsInProgress,
  toAsRegisterTarget,
  toAsTarget,
} from 'utils/apiCalling/api'
import {
  confirmASAlert,
  confirmASCancleAlert,
  noDataAlert,
  normalAlert,
  resultAlert,
} from 'utils/CustomAlert/Alert'

/*********************************************************************
 ** FlaggedReview 전용 A/S mutation 샘플
 ** 기존 api.ts의 requestAsNew/requestAsCancelNew(콜백형)를 react-query mutation으로 이식.
 ** - 순수 API 호출은 reactQueryApi.ts, confirm/검증/알림은 여기서 오케스트레이션
 ** - 성공 시 ['GetCustomerIssues'] 쿼리를 무효화해 목록 자동 갱신
 ** (다른 페이지로 확산할 때 이 패턴을 재사용)
 *********************************************************************/
export const useFlaggedReviewAsMutation = (userInfo: any) => {
  const queryClient = useQueryClient()

  const invalidateList = () => {
    queryClient.invalidateQueries({queryKey: ['GetCustomerIssues']})
  }

  const registerMutation = useMutation({
    mutationFn: postAsRegister,
    onSuccess: async (data: any) => {
      await resultAlert(data)
      if (data?.resultCode === 0) invalidateList()
    },
    onError: async (e: any) => {
      await resultAlert({resultCode: e?.response?.status})
    },
  })

  const cancelMutation = useMutation({
    mutationFn: putAsCancel,
    onSuccess: async (data: any) => {
      await resultAlert(data)
      if (data?.resultCode === 0) invalidateList()
    },
    onError: async (e: any) => {
      await resultAlert({resultCode: e?.response?.status})
    },
  })

  // A/S 등록: 원인 코드 조회 → confirm → 검증 → mutate
  const requestAs = async (row: any) => {
    const causeRes = await getAsCauseType()
    const cause = await confirmASAlert('A/S등록', 'info', causeRes, asTargetLabel(row))
    if (!cause) return
    if (!row) {
      await noDataAlert()
      return
    }
    if (isAsInProgress(row)) {
      await normalAlert('AS가 진행 중인 수용가가 있습니다.')
      return
    }
    registerMutation.mutate({
      asList: [toAsRegisterTarget(row)],
      asId: null,
      causeIdx: cause,
      registerId: userInfo?.userId,
    })
  }

  // A/S 취소: 검증 → confirm → mutate
  const cancelAs = async (row: any) => {
    if (!row) {
      await noDataAlert()
      return
    }
    if (!isAsInProgress(row)) {
      await normalAlert('AS가 진행 중이지 않은 수용가가 존재합니다.')
      return
    }
    if (await confirmASCancleAlert('A/S취소', 'info', asTargetLabel(row))) {
      cancelMutation.mutate({asList: [toAsTarget(row)]})
    }
  }

  return {
    requestAs,
    cancelAs,
    isAsPending: registerMutation.isPending || cancelMutation.isPending,
  }
}
